import React, { useEffect, useRef, useState } from 'react';
import { SunIcon, MoonIcon, ComputerDesktopIcon } from '@heroicons/react/24/outline';
import { useTheme, type ThemeMode } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

interface ThemeToggleButtonProps {
  className?: string;
}

const MODES: { value: ThemeMode; Icon: typeof SunIcon }[] = [
  { value: 'light', Icon: SunIcon },
  { value: 'dark', Icon: MoonIcon },
  { value: 'system', Icon: ComputerDesktopIcon },
];

export const ThemeToggleButton: React.FC<ThemeToggleButtonProps> = ({ className = '' }) => {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = (mode: ThemeMode) => {
    setTheme(mode);
    setIsOpen(false);
  };

  // The trigger reflects the effective appearance (system resolves to light/dark).
  const TriggerIcon = theme === 'system' ? ComputerDesktopIcon : resolvedTheme === 'dark' ? MoonIcon : SunIcon;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-surface-800 dark:hover:text-white"
        title={t('settings.theme.title')}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <TriggerIcon className="h-5 w-5" />
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-1 w-40 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 dark:bg-surface-800 dark:ring-surface-700"
        >
          <div className="overflow-hidden py-1">
            {MODES.map(({ value, Icon }) => (
              <button
                key={value}
                role="menuitemradio"
                aria-checked={theme === value}
                onClick={() => handleSelect(value)}
                className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm ${
                  theme === value
                    ? 'bg-slate-100 text-slate-900 dark:bg-surface-700 dark:text-white'
                    : 'text-slate-700 dark:text-slate-300'
                } hover:bg-slate-100 dark:hover:bg-surface-700`}
              >
                <Icon className="h-5 w-5" />
                {t(`settings.theme.${value}`)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
